import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import { 
    Printer, 
    Save, 
    Plus, 
    Trash2, 
    FileText, 
    Sparkles, 
    Building, 
    RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminOffers = () => {
    const { i18n } = useTranslation();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const isKa = i18n.language === 'ka';
    const printRef = useRef(null);

    const [savedOffers, setSavedOffers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const [offerData, setOfferData] = useState({
        id: null,
        offer_number: `ORBX-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
        date: new Date().toISOString().split('T')[0],
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        client_name: 'გიორგი ბერიძე',
        client_company: 'ინოვაციური ბიზნეს ჯგუფი',
        client_email: 'client@example.com',
        project_name: 'ინოვაციური ვებ პლატფორმა & ციფრული ეკოსისტემა',
        currency: 'GEL',
        notes: 'მოცემული შეთავაზება მოიცავს სრულ ციფრულ პროექტირებას, დიზაინს, ინტეგრაციას, უსაფრთხოების აუდიტსა და 6 თვიან საგარანტიო მხარდაჭერას.',
        terms: '1. ავანსი: 40% ხელშეკრულების გაფორმებისას.\n2. მეორე ეტაპი: 30% დიზაინისა და პროტოტიპის დამტკიცების შემდეგ.\n3. ფინალური გადახდა: 30% პროექტის გაშვებისა და ჩაბარების შემდეგ.\n4. საგარანტიო მომსახურება: 6 თვე უსასყიდლოდ.',
        items: [
            { id: 1, title: 'UI/UX დიზაინის სისტემა & პროტოტიპირება', description: 'Figma-ში სრული Design System, ადაპტური განლაგება მობილურისა და დესკტოპისთვის', qty: 1, price: 3500 },
            { id: 2, title: 'Full-Stack ვებ აპლიკაციის დეველოპმენტი', description: 'React/Next.js frontend, REST API, მონაცემთა ბაზის არქიტექტურა, როლების მართვა', qty: 1, price: 8500 },
            { id: 3, title: 'SEO, კიბერუსაფრთხოების აუდიტი & ოპტიმიზაცია', description: 'WAF ინტეგრაცია, SSL, პენეტრაციული ტესტირება, Core Web Vitals მაღალი სისწრაფე', qty: 1, price: 2000 },
            { id: 4, title: 'Cloud Deployment & DevOps Setup', description: 'CI/CD პაიპლაინები, ავტომატური სარეზერვო ასლები, სერვერის კონფიგურაცია', qty: 1, price: 1500 }
        ]
    });

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchSavedOffers = useCallback(async () => {
        try {
            const res = await fetchWithSecurity(`${API_URL}offers.php`);
            const data = await res.json();
            setSavedOffers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching offers:', err);
        }
    }, []);

    useEffect(() => {
        if (user) fetchSavedOffers();
    }, [user, fetchSavedOffers]);

    const handleItemChange = (index, field, value) => {
        const newItems = [...offerData.items];
        newItems[index] = { ...newItems[index], [field]: value };
        setOfferData({ ...offerData, items: newItems });
    };

    const handleAddItem = () => {
        const newItem = {
            id: Date.now(),
            title: isKa ? 'ახალი სერვისი / ეტაპი' : 'New Service / Deliverable',
            description: '',
            qty: 1,
            price: 1000
        };
        setOfferData({ ...offerData, items: [...offerData.items, newItem] });
    };

    const handleRemoveItem = (index) => {
        const newItems = offerData.items.filter((_, i) => i !== index);
        setOfferData({ ...offerData, items: newItems });
    };

    const totalAmount = offerData.items.reduce((sum, item) => sum + (Number(item.qty) || 0) * (Number(item.price) || 0), 0);

    const handleSaveOffer = async () => {
        if (!offerData.client_name.trim() || !offerData.project_name.trim()) {
            toast.error('გთხოვთ შეავსოთ კლიენტისა და პროექტის სახელი');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...offerData,
                total_amount: totalAmount,
                items: offerData.items
            };
            const res = await fetchWithSecurity(`${API_URL}offers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await res.json();
            if (result.success) {
                toast.success('შეთავაზება წარმატებით შეინახა ბაზაში!');
                setOfferData(prev => ({ ...prev, id: result.id }));
                fetchSavedOffers();
            } else {
                toast.error(result.error || 'შენახვა ვერ მოხერხდა');
            }
        } catch (err) {
            toast.error('შენახვა ვერ მოხერხდა');
        }
        setIsSaving(false);
    };

    const handleLoadOffer = async (id) => {
        try {
            const res = await fetchWithSecurity(`${API_URL}offers.php?id=${id}`);
            const data = await res.json();
            if (data) {
                setOfferData({
                    id: data.id,
                    offer_number: data.offer_number || offerData.offer_number,
                    date: data.created_at ? data.created_at.split(' ')[0] : offerData.date,
                    valid_until: offerData.valid_until,
                    client_name: data.client_name || '',
                    client_company: data.client_company || '',
                    client_email: data.client_email || '',
                    project_name: data.project_name || '',
                    currency: data.currency || 'GEL',
                    notes: data.notes || '',
                    terms: offerData.terms,
                    items: Array.isArray(data.items) && data.items.length > 0 ? data.items : offerData.items
                });
                toast.success('შეთავაზება ჩაიტვირთა რედაქტირებისთვის');
            }
        } catch (err) {
            toast.error('ჩატვირთვა ვერ მოხერხდა');
        }
    };

    const handleDeleteOffer = async (id) => {
        if (!window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ შეთავაზების წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}offers.php?id=${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success('შეთავაზება წაიშალა');
                fetchSavedOffers();
            }
        } catch (err) {
            toast.error('წაშლა ვერ მოხერხდა');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={isKa ? 'შეთავაზებების გენერატორი (PDF)' : 'Proposal Generator (PDF)'}>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
                <div>
                    <h4 className="fw-bold mb-1">{isKa ? 'კომერციული შეთავაზებების გენერატორი' : 'Commercial Proposal Generator'}</h4>
                    <p className="text-muted small mb-0">{isKa ? 'შექმენით და დაბეჭდეთ ოფიციალური შეთავაზება OrbitroniX-ის ბრენდირებული თავფურცლით (Cover Page)' : 'Generate and print official proposals with branded OrbitroniX cover page'}</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="shadcn-btn shadcn-btn-secondary" onClick={handleSaveOffer} disabled={isSaving}>
                        <Save size={15} /> {isSaving ? '...' : (isKa ? 'შენახვა ბაზაში' : 'Save Offer')}
                    </button>
                    <button className="shadcn-btn shadcn-btn-primary" onClick={handlePrint}>
                        <Printer size={15} /> {isKa ? 'PDF ბეჭდვა / ჩამოტვირთვა' : 'Print / Export PDF'}
                    </button>
                </div>
            </div>

            <div className="row g-4">
                {/* Form Controls Sidebar (Hidden in Print) */}
                <div className="col-xl-4 no-print">
                    <div className="shadcn-card mb-4">
                        <div className="shadcn-card-header">
                            <div className="shadcn-card-title d-flex align-items-center gap-2">
                                <Building size={17} className="text-primary" />
                                <span>{isKa ? 'კლიენტის მონაცემები' : 'Client Details'}</span>
                            </div>
                        </div>
                        <div className="p-3">
                            <div className="mb-3">
                                <label className="form-group-label">{isKa ? 'კლიენტის სახელი' : 'Client Name'} *</label>
                                <input
                                    type="text"
                                    className="shadcn-input"
                                    value={offerData.client_name}
                                    onChange={e => setOfferData({ ...offerData, client_name: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-group-label">{isKa ? 'კომპანია / ორგანიზაცია' : 'Company'}</label>
                                <input
                                    type="text"
                                    className="shadcn-input"
                                    value={offerData.client_company}
                                    onChange={e => setOfferData({ ...offerData, client_company: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-group-label">{isKa ? 'ელ. ფოსტა' : 'Email'}</label>
                                <input
                                    type="email"
                                    className="shadcn-input"
                                    value={offerData.client_email}
                                    onChange={e => setOfferData({ ...offerData, client_email: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-group-label">{isKa ? 'პროექტის დასახელება' : 'Project Title'} *</label>
                                <input
                                    type="text"
                                    className="shadcn-input"
                                    value={offerData.project_name}
                                    onChange={e => setOfferData({ ...offerData, project_name: e.target.value })}
                                />
                            </div>
                            <div className="row g-2 mb-3">
                                <div className="col-6">
                                    <label className="form-group-label">{isKa ? 'თარიღი' : 'Date'}</label>
                                    <input
                                        type="date"
                                        className="shadcn-input"
                                        value={offerData.date}
                                        onChange={e => setOfferData({ ...offerData, date: e.target.value })}
                                    />
                                </div>
                                <div className="col-6">
                                    <label className="form-group-label">{isKa ? 'ვალუტა' : 'Currency'}</label>
                                    <select
                                        className="shadcn-select"
                                        value={offerData.currency}
                                        onChange={e => setOfferData({ ...offerData, currency: e.target.value })}
                                    >
                                        <option value="GEL">GEL (₾)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="EUR">EUR (€)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Services / Deliverables Builder */}
                    <div className="shadcn-card mb-4">
                        <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                            <div className="shadcn-card-title d-flex align-items-center gap-2">
                                <FileText size={17} className="text-primary" />
                                <span>{isKa ? 'სერვისები & ეტაპები' : 'Line Items'}</span>
                            </div>
                            <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={handleAddItem}>
                                <Plus size={14} /> {isKa ? 'დამატება' : 'Add Item'}
                            </button>
                        </div>
                        <div className="p-3">
                            {offerData.items.map((item, idx) => (
                                <div key={item.id || idx} className="p-3 rounded-3 bg-light border mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-bold small">#{idx + 1} {item.title || 'პუნქტი'}</span>
                                        {offerData.items.length > 1 && (
                                            <button 
                                                className="btn btn-sm btn-outline-danger border-0 p-1"
                                                onClick={() => handleRemoveItem(idx)}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        type="text"
                                        className="shadcn-input mb-2"
                                        placeholder="სერვისის სახელი"
                                        value={item.title}
                                        onChange={e => handleItemChange(idx, 'title', e.target.value)}
                                    />
                                    <textarea
                                        className="shadcn-input mb-2"
                                        rows="2"
                                        placeholder="დეტალური აღწერა / ფუნქციები"
                                        value={item.description}
                                        onChange={e => handleItemChange(idx, 'description', e.target.value)}
                                    />
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <label className="form-group-label smallest">{isKa ? 'რაოდენობა' : 'Qty'}</label>
                                            <input
                                                type="number"
                                                className="shadcn-input"
                                                value={item.qty}
                                                onChange={e => handleItemChange(idx, 'qty', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-group-label smallest">{isKa ? 'ფასი' : 'Price'}</label>
                                            <input
                                                type="number"
                                                className="shadcn-input"
                                                value={item.price}
                                                onChange={e => handleItemChange(idx, 'price', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Saved Offers List */}
                    <div className="shadcn-card">
                        <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                            <div className="shadcn-card-title d-flex align-items-center gap-2">
                                <Sparkles size={17} className="text-primary" />
                                <span>{isKa ? 'შენახული შეთავაზებები' : 'Saved Offers'}</span>
                            </div>
                            <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchSavedOffers}>
                                <RefreshCw size={12} />
                            </button>
                        </div>
                        <div className="p-3">
                            {savedOffers.length === 0 ? (
                                <p className="text-muted small mb-0 text-center py-2">{isKa ? 'შენახული შეთავაზებები არ არის' : 'No saved offers'}</p>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {savedOffers.map(o => (
                                        <div key={o.id} className="d-flex justify-content-between align-items-center p-2 rounded bg-light border">
                                            <div>
                                                <div className="fw-bold small">{o.client_name}</div>
                                                <div className="smallest text-muted">{o.offer_number} • {o.total_amount} {o.currency}</div>
                                            </div>
                                            <div className="d-flex gap-1">
                                                <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm p-1" onClick={() => handleLoadOffer(o.id)}>
                                                    {isKa ? 'გახსნა' : 'Open'}
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger border-0 p-1" onClick={() => handleDeleteOffer(o.id)}>
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Printable Document Preview Area */}
                <div className="col-xl-8">
                    <div className="offer-preview-container" ref={printRef}>
                        
                        {/* ═════════ PAGE 1: LUXURY COVER PAGE ═════════ */}
                        <div className="proposal-page proposal-cover-page">
                            <div className="cover-grid-bg"></div>
                            
                            {/* Brand Header */}
                            <div className="cover-header d-flex justify-content-between align-items-center">
                                <div className="brand-logo-text">
                                    <span className="logo-gradient">ORBITRONIX</span>
                                    <span className="logo-sub">ENGINEERING & DIGITAL SOLUTIONS</span>
                                </div>
                                <div className="proposal-badge">
                                    CONFIDENTIAL PROPOSAL
                                </div>
                            </div>

                            {/* Cover Center Content */}
                            <div className="cover-center">
                                <div className="cover-tag mb-3">
                                    <span>{offerData.offer_number}</span>
                                </div>
                                <h1 className="cover-title mb-4">
                                    {isKa ? 'ტექნიკური & ფინანსური შეთავაზება' : 'Technical & Commercial Proposal'}
                                </h1>
                                <p className="cover-subtitle mb-5">
                                    {offerData.project_name}
                                </p>
                                <div className="cover-divider mb-5"></div>
                            </div>

                            {/* Cover Footer Details */}
                            <div className="cover-footer">
                                <div className="row g-4">
                                    <div className="col-6">
                                        <div className="cover-meta-box">
                                            <span className="meta-label">{isKa ? 'მომზადებულია კლიენტისთვის:' : 'Prepared For:'}</span>
                                            <h5 className="meta-value">{offerData.client_name}</h5>
                                            <p className="meta-sub">{offerData.client_company}</p>
                                            <p className="meta-sub">{offerData.client_email}</p>
                                        </div>
                                    </div>
                                    <div className="col-6 text-end">
                                        <div className="cover-meta-box">
                                            <span className="meta-label">{isKa ? 'შემსრულებელი:' : 'Prepared By:'}</span>
                                            <h5 className="meta-value">OrbitroniX Digital Lab</h5>
                                            <p className="meta-sub">info@orbitronix.ge</p>
                                            <p className="meta-sub">{offerData.date}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ═════════ PAGE 2: SCOPE, PRICING & TERMS ═════════ */}
                        <div className="proposal-page proposal-content-page">
                            {/* Page 2 Header */}
                            <div className="inner-page-header d-flex justify-content-between align-items-center pb-3 mb-4 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold text-dark">ORBITRONIX</span>
                                    <span className="text-muted">| {offerData.project_name}</span>
                                </div>
                                <div className="text-muted small">
                                    {offerData.offer_number}
                                </div>
                            </div>

                            {/* Scope Overview */}
                            <div className="mb-4">
                                <h5 className="section-heading mb-2">1. {isKa ? 'პროექტის მიმოხილვა & მიზანი' : 'Project Scope & Objectives'}</h5>
                                <p className="text-secondary small leading-relaxed">
                                    {offerData.notes}
                                </p>
                            </div>

                            {/* Pricing & Deliverables Table */}
                            <div className="mb-4">
                                <h5 className="section-heading mb-3">2. {isKa ? 'სამუშაო ეტაპები & ღირებულება' : 'Deliverables & Cost Breakdown'}</h5>
                                <table className="proposal-table w-100">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '45%' }}>{isKa ? 'სერვისის / ეტაპის აღწერა' : 'Deliverable'}</th>
                                            <th className="text-center" style={{ width: '15%' }}>{isKa ? 'რაოდ.' : 'Qty'}</th>
                                            <th className="text-end" style={{ width: '20%' }}>{isKa ? 'ერთეულის ფასი' : 'Unit Price'}</th>
                                            <th className="text-end" style={{ width: '20%' }}>{isKa ? 'სულ' : 'Total'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {offerData.items.map((it, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <div className="fw-bold text-dark">{it.title}</div>
                                                    {it.description && <div className="text-muted smallest mt-1">{it.description}</div>}
                                                </td>
                                                <td className="text-center">{it.qty}</td>
                                                <td className="text-end">{Number(it.price).toLocaleString()} {offerData.currency}</td>
                                                <td className="text-end fw-bold">{(Number(it.qty) * Number(it.price)).toLocaleString()} {offerData.currency}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="3" className="text-end fw-bold py-3 fs-6">
                                                {isKa ? 'ჯამური საინვესტიციო ღირებულება:' : 'Total Investment:'}
                                            </td>
                                            <td className="text-end fw-extrabold py-3 fs-5 text-primary">
                                                {totalAmount.toLocaleString()} {offerData.currency}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Terms & Conditions */}
                            <div className="mb-4">
                                <h5 className="section-heading mb-2">3. {isKa ? 'პირობები & ანგარიშსწორების გრაფიკი' : 'Terms & Payment Schedule'}</h5>
                                <div className="terms-box p-3 rounded-2 bg-light border small text-secondary" style={{ whiteSpace: 'pre-line' }}>
                                    {offerData.terms}
                                </div>
                            </div>

                            {/* Signature / Acceptance Block */}
                            <div className="signature-section mt-5 pt-4 border-top">
                                <div className="row g-4 align-items-end">
                                    <div className="col-6">
                                        <p className="smallest text-muted text-uppercase fw-bold mb-4">{isKa ? 'დამკვეთის თანხმობა / ხელმოწერა:' : 'Accepted By (Client):'}</p>
                                        <div className="signature-line"></div>
                                        <p className="small fw-bold mb-0">{offerData.client_name}</p>
                                        <p className="smallest text-muted">{offerData.client_company}</p>
                                    </div>
                                    <div className="col-6 text-end">
                                        <p className="smallest text-muted text-uppercase fw-bold mb-4">{isKa ? 'შემსრულებელი / OrbitroniX:' : 'OrbitroniX Engineering:'}</p>
                                        <div className="signature-line ms-auto"></div>
                                        <p className="small fw-bold mb-0">OrbitroniX Team</p>
                                        <p className="smallest text-muted">Authorised Signature & Stamp</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <style>{`
                .offer-preview-container {
                    background: #525659;
                    padding: 30px;
                    border-radius: 12px;
                    display: flex;
                    flex-direction: column;
                    gap: 30px;
                    align-items: center;
                }

                .proposal-page {
                    width: 210mm;
                    min-height: 297mm;
                    background: #ffffff;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
                    box-sizing: border-box;
                    position: relative;
                }

                /* COVER PAGE STYLING */
                .proposal-cover-page {
                    background: linear-gradient(135deg, #0b0f19 0%, #1e1b4b 50%, #312e81 100%);
                    color: #ffffff;
                    padding: 60px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    overflow: hidden;
                }

                .cover-grid-bg {
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-image: radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px);
                    background-size: 24px 24px;
                    pointer-events: none;
                }

                .logo-gradient {
                    font-size: 1.8rem;
                    font-weight: 900;
                    letter-spacing: 3px;
                    background: linear-gradient(135deg, #ffffff, #a5b4fc);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    display: block;
                }

                .logo-sub {
                    font-size: 0.65rem;
                    letter-spacing: 2px;
                    color: #a5b4fc;
                    font-weight: 600;
                    display: block;
                }

                .proposal-badge {
                    background: rgba(255,255,255,0.1);
                    border: 1px solid rgba(255,255,255,0.2);
                    padding: 6px 14px;
                    border-radius: 20px;
                    font-size: 0.7rem;
                    letter-spacing: 1.5px;
                    font-weight: 700;
                    color: #e0e7ff;
                }

                .cover-tag span {
                    display: inline-block;
                    background: rgba(99, 102, 241, 0.3);
                    color: #c7d2fe;
                    padding: 6px 14px;
                    border-radius: 6px;
                    font-family: monospace;
                    font-size: 0.85rem;
                    border: 1px solid rgba(99, 102, 241, 0.5);
                }

                .cover-title {
                    font-size: 2.6rem;
                    font-weight: 800;
                    line-height: 1.2;
                    letter-spacing: -0.5px;
                }

                .cover-subtitle {
                    font-size: 1.25rem;
                    color: #c7d2fe;
                    max-width: 80%;
                }

                .cover-divider {
                    width: 80px;
                    height: 4px;
                    background: #6366f1;
                    border-radius: 2px;
                }

                .cover-meta-box .meta-label {
                    display: block;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #94a3b8;
                    margin-bottom: 6px;
                }

                .cover-meta-box .meta-value {
                    font-size: 1.2rem;
                    font-weight: 700;
                    margin-bottom: 4px;
                }

                .cover-meta-box .meta-sub {
                    margin-bottom: 2px;
                    font-size: 0.85rem;
                    color: #cbd5e1;
                }

                /* CONTENT PAGE STYLING */
                .proposal-content-page {
                    padding: 50px;
                    color: #1e293b;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                }

                .section-heading {
                    font-weight: 800;
                    font-size: 1.05rem;
                    color: #0f172a;
                    border-left: 3px solid #4f46e5;
                    padding-left: 10px;
                }

                .proposal-table {
                    border-collapse: collapse;
                }

                .proposal-table th {
                    background: #f8fafc;
                    padding: 12px;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #475569;
                    border-bottom: 2px solid #e2e8f0;
                }

                .proposal-table td {
                    padding: 12px;
                    font-size: 0.85rem;
                    border-bottom: 1px solid #f1f5f9;
                }

                .signature-line {
                    width: 180px;
                    height: 1px;
                    background: #94a3b8;
                    margin-bottom: 8px;
                }

                /* PRINT RULES */
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .no-print, .shadcn-header, .shadcn-sidebar, .toast {
                        display: none !important;
                    }
                    .offer-preview-container, .offer-preview-container * {
                        visibility: visible;
                    }
                    .offer-preview-container {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        background: none !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        gap: 0 !important;
                    }
                    .proposal-page {
                        width: 100% !important;
                        height: 100vh !important;
                        box-shadow: none !important;
                        margin: 0 !important;
                        page-break-after: always !important;
                        break-after: page !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                }
            `}</style>
        </AdminLayout>
    );
};

export default AdminOffers;
